import React, { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Link,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';

import { Lane } from '../../interfaces/services/lanes';
import { formatCurrency, formatNumber } from '../../utils/formattingUtils';
import { buildLaneLink } from '../../utils/navigationHelpers';
import { useFilters } from '../Filters/useFilters';

const TopLanesTable = ({
    laneType,
    loadingTopLanes,
    topLanes,
    entityName,
}: {
    laneType: 'inbound' | 'outbound';
    loadingTopLanes: boolean;
    topLanes: Lane[];
    /** name to use in error messages */
    entityName: string | null;
}): React.JSX.Element => {
    const filters = useFilters();

    const renderTopLanes = (): ReactNode => {
        if (loadingTopLanes) {
            return Array.from(new Array(5)).map((value, index) => {
                return (
                    <TableRow key={index}>
                        <TableCell colSpan={2}><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                    </TableRow>
                );
            });
        }

        if (topLanes.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={4}>
                        {entityName ? `No ${laneType} lanes ${laneType === 'inbound' ? 'to' : 'from'} ${entityName} found` : 'No Data'}
                    </TableCell>
                </TableRow>
            );
        }

        return topLanes.map((lane, index) => {
            return (
                <TableRow key={`top-${laneType}-lanes-row-${index}`}>
                    <TableCell colSpan={2}>
                        <Link
                            component={RouterLink}
                            to={buildLaneLink(lane.pickup_service_area_id, lane.delivery_service_area_id, filters)}
                        >
                            {`${lane.pickup_service_area_id} - ${lane.delivery_service_area_id}`}
                        </Link>
                    </TableCell>
                    <TableCell>
                        {formatNumber(lane.freight_order_count, 0, 0)}
                    </TableCell>
                    <TableCell>
                        {formatCurrency(lane.linehaul_revenue_usd_amount)}
                    </TableCell>
                </TableRow>
            );
        });
    };

    return (
        <TableContainer>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell colSpan={2}>Lane</TableCell>
                        <TableCell>Orders</TableCell>
                        <TableCell>Revenue</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody style={{ overflow: 'auto' }}>
                    {renderTopLanes()}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default TopLanesTable;
